import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,Paper
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import LinkedInConnect from "./LinkedInConnect";
import { 
  LinkedIn as LinkedInIcon, 
  CheckCircle,
  Error as ErrorIcon,
  PostAdd,
  ArrowBack,
  Settings
} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const JobPosting = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const jobFromState = location.state?.job;
  
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [company_id, setCompanyId] = useState(null);
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [postToLinkedIn, setPostToLinkedIn] = useState(true);
  const [openPageSelection, setOpenPageSelection] = useState(false);
  const [selectedPagesForPost, setSelectedPagesForPost] = useState([]);
  const [postingToPages, setPostingToPages] = useState(false);
  const [postResults, setPostResults] = useState(null);
  const [customDepartment, setCustomDepartment] = useState('');

  const standardDepartments = [
    "Engineering", "Marketing", "Sales", "Sales & Marketing", "Finance", 
    "Human Resources", "Operations", "IT Support", "Legal", "Product Management", 
    "Design", "Customer Service", "Quality Assurance", "Research and Development", 
    "Procurement", "Logistics", "Business Development", "Administration", 
    "Training", "Security", "Public Relations"
  ];

  const [requisition, setRequisition] = useState({
    jobId: "",
    jobTitle: "",
    department: "",
    location: "",
    employmentType: "",
    noOfPositions: "",
    experience: "",
    budget: "",
    jobClosedDate: "",
    hiringManagerName: "",
    hiringManagerEmail: "", 
    description: "",
  });

  // Get company_id from token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCompanyId(payload.company_id);
      } catch (error) {
        //console.error("Error parsing token:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (jobFromState) {
      const isStandard = standardDepartments.includes(jobFromState.department);
      setRequisition({
        ...jobFromState,
        department: isStandard ? jobFromState.department : (jobFromState.department ? "Other" : ""),
        jobClosedDate: formatDateForInput(jobFromState.jobClosedDate)
      });
      if (!isStandard && jobFromState.department) {
        setCustomDepartment(jobFromState.department);
      }
      return;
    }
    
    if (id) {
      fetchJob(id);
    }
  }, [id, jobFromState]);

  // Check LinkedIn status
  const checkLinkedInStatus = async () => {
    if (!company_id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/linkedin/status`,
        { headers: { Authorization: `Bearer ${token}`,
       } }
      );
      
      setLinkedinStatus(response.data);
      
      // Auto-select LinkedIn posting if connected and has selected pages
      if (response.data.connected && 
          response.data.token_status === 'valid' && 
          response.data.selected_pages > 0) {
        setPostToLinkedIn(true);
        // Initialize selected pages for posting
        const selected = response.data.pages
          .filter(page => page.is_selected)
          .map(page => page.page_urn);
        setSelectedPagesForPost(selected);
      } else {
        setPostToLinkedIn(false);
        setSelectedPagesForPost([]);
      }
    } catch (error) {
      //console.error("Error checking LinkedIn status:", error);
      setLinkedinStatus(null);
    }
  };

  useEffect(() => {
    if (company_id) {
      checkLinkedInStatus();
    }
  }, [company_id]);

  const fetchJob = async (jobId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/jobpost/${jobId}`, {
        headers: { Authorization: `Bearer ${token}`,
        Authorization: `Bearer ${token}`,
      }
      });
      
      const job = response.data.job;
      const isStandard = standardDepartments.includes(job.department);
      setRequisition({
        ...job,
        department: isStandard ? job.department : (job.department ? "Other" : ""),
        jobClosedDate: formatDateForInput(job.jobClosedDate)
      });
      if (!isStandard && job.department) {
        setCustomDepartment(job.department);
      }
      setLoading(false);
    } catch (error) {
      //console.error("Error fetching job:", error);
      setAlert({
        open: true,
        message: "Failed to fetch job details",
        severity: "error"
      });
      setLoading(false);
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return "";
    
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "";
      
      const offsetMs = dateObj.getTimezoneOffset() * 60000;
      const localDate = new Date(dateObj.getTime() - offsetMs);
      return localDate.toISOString().split("T")[0];
    } catch (error) {
      //console.error("Error formatting date:", error);
      return "";
    }
  };

  const validateNumericField = (value, fieldName) => {
    const numericRegex = /^[0-9.-]+$/;
    if (value && !numericRegex.test(value)) {
      return `${fieldName} should contain only numbers`;
    }
    return "";
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFieldErrors(prev => ({ ...prev, [name]: "" }));
    
    if (name === 'experience' || name === 'budget') {
      const error = validateNumericField(value, name === 'experience' ? 'Experience' : 'Budget');
      if (error) {
        setFieldErrors(prev => ({ ...prev, [name]: error }));
        return;
      }
    }
    
    setRequisition({ ...requisition, [name]: value });
  };

  const handleAIGenerate = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAlert({ open: true, message: "Please log in first", severity: "error" });
      return;
    }

    const requiredFields = ['jobTitle', 'department', 'experience', 'location', 'employmentType', 'budget'];
    const missingFields = requiredFields.filter(field => !requisition[field]);
    
    if (missingFields.length > 0) {
      setAlert({
        open: true,
        message: `Please fill required fields: ${missingFields.join(', ')}`,
        severity: "warning",
      });
      return;
    }

    try {
      setAiGenerating(true);
      
      const config = { 
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        }
      };

      const response = await axios.post(
        `${API_URL}/api/jobpost/generate-description`, 
        {
          jobTitle: requisition.jobTitle,
          department: requisition.department === 'Other' ? customDepartment.trim() : requisition.department,
          location: requisition.location,
          employmentType: requisition.employmentType,
          experience: requisition.experience,
          budget: requisition.budget
        },
        config
      );

      const generatedDescription = response.data.description || "AI generation failed. Please try again.";

      setRequisition((prev) => ({
        ...prev,
        description: generatedDescription
      }));

      setAlert({
        open: true,
        message: "✨ Job description generated successfully!",
        severity: "success"
      });
      
    } catch (err) {
      //console.error("AI generation error:", err);
      
      const errorData = err.response?.data;

      // 1. IF PLAN DOES NOT INCLUDE AI (e.g., Basic Plan with 0 tokens)
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setAlert({
          open: true,
          message: errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
          severity: "warning"
        });

      } 
      // 2. IF TOKENS ARE EXHAUSTED
      else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setAlert({
          open: true,
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`,
          severity: "warning"
        });
      } 
      else {
        let errorMessage = "Failed to generate job description. Please try again.";
        if (errorData?.message) {
          errorMessage = errorData.message;
        }
        setAlert({
          open: true,
          message: errorMessage,
          severity: "error"
        });
      }
    } finally {
      setAiGenerating(false);
    }
  };

  // Post to selected LinkedIn pages
  const postToLinkedInPages = async (jobData, selectedPageUrns) => {
    try {
      setPostingToPages(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Get page names for the selected URNs
      const selectedPages = linkedinStatus.pages.filter(
        page => selectedPageUrns.includes(page.page_urn)
      );

      const linkedinText = `
📢 We are #hiring for the role of ${jobData.jobTitle} in the ${jobData.department} department.

📌 Job Details:
• 👤 Experience: ${jobData.experience} years
• 📍 Location: ${jobData.location}
• 🔢 Job ID: ${jobData.jobId}

${jobData.description}

#Careers #JobOpportunity #WeAreHiring #RecruitmentCareers #HiringNow
`;

      const response = await axios.post(
        `${API_URL}/api/jobpost/share-linkedin`,
        {
          company_id,
          jobId: jobData.jobId,
          jobTitle: jobData.jobTitle,
          department: jobData.department,
          location: jobData.location,
          experience: jobData.experience,
          description: jobData.description
        },
        config
      );

      setPostResults({
        success: true,
        message: `Posted to ${selectedPages.length} LinkedIn page(s) successfully!`,
        pages: selectedPages.map(page => ({
          name: page.page_name,
          success: true
        }))
      });

      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      //console.error("LinkedIn posting error:", error);
      
      setPostResults({
        success: false,
        message: error.response?.data?.message || "Failed to post to LinkedIn",
        pages: []
      });

      return {
        success: false,
        message: error.response?.data?.message || "Failed to post to LinkedIn"
      };
    } finally {
      setPostingToPages(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('token');
    if (!token) {
      setAlert({ open: true, message: "Please log in first", severity: "error" });
      return;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    
    const hasFieldErrors = Object.values(fieldErrors).some(error => error !== "");
    if (hasFieldErrors) {
      setAlert({
        open: true,
        message: "Please fix the validation errors before submitting",
        severity: "error"
      });
      return;
    }
    
    const requiredFields = [
      'jobId', 'jobTitle', 'department', 'location', 'employmentType',
      'noOfPositions', 'experience', 'budget', 'jobClosedDate',
      'hiringManagerName', 'hiringManagerEmail'
    ];
    
    const missingFields = requiredFields.filter(field => !requisition[field]);
    if (missingFields.length > 0) {
      setAlert({
        open: true,
        message: `Missing required fields: ${missingFields.join(', ')}`,
        severity: "error"
      });
      return;
    }

    if (requisition.department === 'Other' && !customDepartment.trim()) {
      setAlert({
        open: true,
        message: "Please specify your custom department",
        severity: "error"
      });
      return;
    }

    if (!validateEmail(requisition.hiringManagerEmail)) {
      setAlert({
        open: true,
        message: "Please enter a valid email for the hiring manager",
        severity: "error"
      });
      return;
    }

    // If LinkedIn posting is enabled, check if we need page selection
    if (postToLinkedIn && linkedinStatus?.connected) {
      if (linkedinStatus.selected_pages > 1) {
        // Open page selection dialog for multiple pages
        setOpenPageSelection(true);
        return;
      } else if (linkedinStatus.selected_pages === 0) {
        setAlert({
          open: true,
          message: "No LinkedIn pages selected for posting. Please manage pages in LinkedIn settings.",
          severity: "warning"
        });
        return;
      }
    }

    // If only one page or no selection needed, proceed directly
    await saveJobAndPost();
  };

  const saveJobAndPost = async () => {
    if (requisition.department === 'Other' && !customDepartment.trim()) {
      setAlert({
        open: true,
        message: "Please specify your custom department",
        severity: "error"
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } 
      };
      
      const jobData = {
        jobId: requisition.jobId?.trim(),
        jobTitle: requisition.jobTitle?.trim(),
        department: requisition.department === 'Other' ? customDepartment.trim() : requisition.department?.trim(),
        location: requisition.location?.trim(),
        employmentType: requisition.employmentType?.trim(),
        noOfPositions: parseInt(requisition.noOfPositions, 10),
        experience: requisition.experience?.trim(),
        budget: requisition.budget?.trim(),
        jobClosedDate: requisition.jobClosedDate,
        hiringManagerName: requisition.hiringManagerName?.trim(),
        hiringManagerEmail: requisition.hiringManagerEmail?.trim().toLowerCase(),
        description: requisition.description?.trim() || ""
      };

      const jobId = jobFromState?.id || id;
      let response;

      // First, create/update the job
      if (jobId) {
        response = await axios.put(
          `${API_URL}/api/jobpost/update/${jobId}`, 
          jobData, 
          config,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
      } else {
        response = await axios.post(
          `${API_URL}/api/jobpost/create`, 
          jobData, 
          config,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
      }

      let successMessage = response.data.message || "Operation successful";
      let linkedinResult = null;

      // Then, post to LinkedIn if enabled and connected
      if (postToLinkedIn && linkedinStatus?.connected && selectedPagesForPost.length > 0) {
        linkedinResult = await postToLinkedInPages(jobData, selectedPagesForPost);
        
        if (linkedinResult.success) {
          successMessage += ` and posted to ${selectedPagesForPost.length} LinkedIn page(s)`;
        } else {
          successMessage += ` (LinkedIn posting failed: ${linkedinResult.message})`;
        }
      } else if (postToLinkedIn && (!linkedinStatus?.connected || selectedPagesForPost.length === 0)) {
        successMessage += " (LinkedIn not connected or no pages selected)";
      }

      setAlert({ 
        open: true, 
        message: successMessage, 
        severity: "success" 
      });
      
      setTimeout(() => navigate("/jobview"), 1500);
      
    } catch (error) {
      //console.error("Error:", error);
      
      let errorMessage = "Operation failed";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      // Show inline error on the Job ID field for duplicate job ID
      if (error.response?.data?.error === 'DUPLICATE_JOB_ID') {
        setFieldErrors(prev => ({ ...prev, jobId: errorMessage }));
      }
      
      setAlert({
        open: true,
        message: errorMessage,
        severity: "error"
      });
    } finally {
      setLoading(false);
      setOpenPageSelection(false);
    }
  };

  // Handle page selection for posting
  const handlePageSelectForPost = (pageUrn) => {
    setSelectedPagesForPost(prev => {
      if (prev.includes(pageUrn)) {
        return prev.filter(urn => urn !== pageUrn);
      } else {
        return [...prev, pageUrn];
      }
    });
  };

  const handleSelectAllForPost = () => {
    const allSelected = linkedinStatus.pages
      .filter(page => page.is_selected)
      .map(page => page.page_urn);
    setSelectedPagesForPost(allSelected);
  };

  const handleDeselectAllForPost = () => {
    setSelectedPagesForPost([]);
  };

  return (
    <div className="container">
      {/* LinkedIn Connection Component */}
      {company_id && (
        <LinkedInConnect 
          companyId={company_id} 
          onConnectionChange={(isConnected) => {
            checkLinkedInStatus();
          }}
        />
      )}

      {/* Post Results Dialog */}
      {/* <Dialog open={!!postResults} onClose={() => setPostResults(null)}>
        <DialogTitle>
          {postResults?.success ? '✅ Posted Successfully' : '❌ Posting Failed'}
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>{postResults?.message}</Typography>
          {postResults?.pages && postResults.pages.length > 0 && (
            <List>
              {postResults.pages.map((page, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {page.success ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={page.name}
                    secondary={page.success ? 'Posted successfully' : 'Failed to post'}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostResults(null)}>Close</Button>
        </DialogActions>
      </Dialog> */}

      {/* LinkedIn Page Selection Dialog */}
      {/* <Dialog open={openPageSelection} onClose={() => setOpenPageSelection(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Select LinkedIn Pages for Posting
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose which LinkedIn pages should receive this job posting:
          </Typography>
          
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Button 
              size="small" 
              onClick={handleSelectAllForPost}
              startIcon={<CheckCircle />}
            >
              Select All
            </Button>
            <Button 
              size="small" 
              onClick={handleDeselectAllForPost}
              startIcon={<ErrorIcon />}
            >
              Deselect All
            </Button>
          </Box>
          
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {linkedinStatus?.pages
              .filter(page => page.is_selected)
              .map(page => (
                <ListItem
                  key={page.page_urn}
                  sx={{
                    border: '1px solid',
                    borderColor: selectedPagesForPost.includes(page.page_urn) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: selectedPagesForPost.includes(page.page_urn) ? 'primary.50' : 'background.paper'
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedPagesForPost.includes(page.page_urn)}
                      onChange={() => handlePageSelectForPost(page.page_urn)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={page.page_name}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {page.page_urn}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
          </List>
          
          <Typography variant="caption" color="text.secondary">
            {selectedPagesForPost.length} of {linkedinStatus?.selected_pages || 0} pages selected
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPageSelection(false)}>Cancel</Button>
          <Button 
            onClick={saveJobAndPost} 
            variant="contained" 
            disabled={selectedPagesForPost.length === 0 || postingToPages}
            startIcon={postingToPages ? <CircularProgress size={20} /> : <PostAdd />}
          >
            {postingToPages ? 'Posting...' : `Post to ${selectedPagesForPost.length} Page(s)`}
          </Button>
        </DialogActions>
      </Dialog> */}
      
      <Card
        sx={{
          maxWidth: 1500,
          mt: 2,
          mx: 3,
          p: 3,
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'grey.300',
          boxShadow: 4,
        }}
      >
        <CardHeader
          title={
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight="bold">
                {(jobFromState || id) ? "Edit Job Posting" : "Job Posting"}
              </Typography>
              
              {/* LinkedIn Posting Status */}
              {/* {linkedinStatus?.connected && (
                <Box display="flex" alignItems="center" gap={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LinkedInIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {linkedinStatus.selected_pages} page(s) selected
                    </Typography>
                  </Box>
                  <Tooltip title="Toggle LinkedIn posting">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={postToLinkedIn}
                          onChange={(e) => setPostToLinkedIn(e.target.checked)}
                          color="primary"
                          disabled={linkedinStatus.selected_pages === 0}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Post to LinkedIn
                        </Typography>
                      }
                    />
                  </Tooltip>
                  {linkedinStatus.selected_pages === 0 && postToLinkedIn && (
                    <Chip 
                      label="No pages selected" 
                      color="warning" 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
              )} */}
            </Box>
          }
        />

        <CardContent>
          {loading && !requisition.jobId ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Form fields remain the same as before */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job ID"
                    name="jobId"
                    value={requisition.jobId}
                    onChange={handleChange}
                    required
                    disabled={!!(jobFromState || id)}  
                    InputProps={{
                      readOnly: !!(jobFromState || id), 
                    }}
                    error={!!fieldErrors.jobId}
                    helperText={fieldErrors.jobId || ""}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    name="jobTitle"
                    value={requisition.jobTitle}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Department</InputLabel>
                    <Select
                      label="Department"
                      name="department"
                      value={requisition.department}
                      onChange={handleChange}
                      required
                    >
                      <MenuItem value="">Select Department</MenuItem>
                      <MenuItem value="Engineering">Engineering</MenuItem>
                      <MenuItem value="Marketing">Marketing</MenuItem>
                      <MenuItem value="Sales">Sales</MenuItem>
                      <MenuItem value="Sales & Marketing">Sales & Marketing</MenuItem>
                      <MenuItem value="Finance">Finance</MenuItem>
                      <MenuItem value="Human Resources">Human Resources</MenuItem>
                      <MenuItem value="Operations">Operations</MenuItem>
                      <MenuItem value="IT Support">IT Support</MenuItem>
                      <MenuItem value="Legal">Legal</MenuItem>
                      <MenuItem value="Product Management">Product Management</MenuItem>
                      <MenuItem value="Design">Design</MenuItem>
                      <MenuItem value="Customer Service">Customer Service</MenuItem>
                      <MenuItem value="Quality Assurance">Quality Assurance</MenuItem>
                      <MenuItem value="Research and Development">Research and Development</MenuItem>
                      <MenuItem value="Procurement">Procurement</MenuItem>
                      <MenuItem value="Logistics">Logistics</MenuItem>
                      <MenuItem value="Business Development">Business Development</MenuItem>
                      <MenuItem value="Administration">Administration</MenuItem>
                      <MenuItem value="Training">Training</MenuItem>
                      <MenuItem value="Security">Security</MenuItem>
                      <MenuItem value="Public Relations">Public Relations</MenuItem>
                      <MenuItem value="Other">Other (Custom Department)</MenuItem>
                    </Select>
                  </FormControl>
                  {requisition.department === 'Other' && (
                    <TextField
                      fullWidth
                      required
                      label="Custom Department"
                      value={customDepartment}
                      onChange={(e) => setCustomDepartment(e.target.value)}
                      variant="outlined"
                      sx={{ mt: 2 }}
                    />
                  )}
                </Grid>
                
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={requisition.location}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                
                 
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Employment Type</InputLabel>
                    <Select
                      name="employmentType"
                      label="Employment Type"
                      value={requisition.employmentType}
                      onChange={handleChange}
                      required
                    >
                      <MenuItem value="">Select Employment Type</MenuItem>
                      <MenuItem value="Full Time">Full Time</MenuItem>
                      <MenuItem value="Part Time">Part Time</MenuItem>
                      <MenuItem value="Contract">Contract</MenuItem>
                      <MenuItem value="Consultant">Consultant</MenuItem>
                      <MenuItem value="Internship">Internship</MenuItem>
                      <MenuItem value="Temporary">Temporary</MenuItem>
                      <MenuItem value="Freelance">Freelance</MenuItem>
                      <MenuItem value="Apprenticeship">Apprenticeship</MenuItem>
                      <MenuItem value="Remote">Remote</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Number of Positions"
                    name="noOfPositions"
                    type="number"
                    value={requisition.noOfPositions}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Experience (in years)"
                    name="experience"
                    value={requisition.experience}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    error={!!fieldErrors.experience}
                    helperText={fieldErrors.experience}
                    placeholder="e.g., 2-5 or 3"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Salary Range"
                    name="budget"
                    value={requisition.budget}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    error={!!fieldErrors.budget}
                    helperText={fieldErrors.budget}
                    placeholder="e.g., 50000-80000"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job Closed Date"
                    name="jobClosedDate"
                    type="date"
                    value={requisition.jobClosedDate}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
  <TextField
    fullWidth
    label="Hiring Manager"
    name="hiringManagerName"
    value={requisition.hiringManagerName}
    onChange={(e) => {
      const value = e.target.value;
      if (/^[a-zA-Z\s]*$/.test(value)) {
        handleChange(e);
      }
    }}
    required
    variant="outlined"
    inputProps={{
      pattern: "[A-Za-z ]*",
      title: "Only alphabets and spaces are allowed",
    }}
  />
</Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hiring Manager Email"
                    name="hiringManagerEmail"
                    type="email"
                    value={requisition.hiringManagerEmail}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    placeholder="example@company.com"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Job Description"
                    name="description"
                    value={requisition.description}
                    onChange={handleChange}
                    variant="outlined"
                    multiline
                    rows={6}
                    placeholder="Leave empty to auto-generate with AI, or write your own description"
                  />
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ mt: 2 }}
                    onClick={handleAIGenerate}
                    disabled={aiGenerating}
                  >
                    {aiGenerating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : "✨ "}
                    {aiGenerating ? "Generating..." : "Generate with AI"}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    💡 Fill in the job details above, then click "Generate with AI" to create a professional job description automatically.
                  </Typography>
                </Grid>
                
                {/* LinkedIn Posting Info */}
                {/* {postToLinkedIn && linkedinStatus?.connected && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <LinkedInIcon color="primary" />
                        <Box flex={1}>
                          <Typography variant="subtitle2">
                            This job will be posted to {linkedinStatus.selected_pages} LinkedIn page(s)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {linkedinStatus.selected_pages > 1 
                              ? "You can select specific pages before posting"
                              : "Will be posted to your connected page"}
                          </Typography>
                        </Box>
                        {linkedinStatus.selected_pages > 1 && (
                          <Button
                            size="small"
                            onClick={() => setOpenPageSelection(true)}
                            startIcon={<Settings />}
                          >
                            Manage Pages
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                )} */}
                
                <Grid item xs={12} textAlign="center">
                  <Button 
                    type="submit" 
                    variant="contained" 
                    sx={{ mr: 2 }}
                    disabled={loading || aiGenerating || postingToPages}
                    startIcon={loading || postingToPages ? <CircularProgress size={24} /> : null}
                    onClick={saveJobAndPost}
                  >
                    {loading ? 'Saving...' : 
                     postingToPages ? 'Posting...' : 
                     (jobFromState || id) ? "Update Job Posting" : "Create Job Posting"}
                  </Button>
                  <Button 
                    onClick={() => navigate("/jobview")}
                    disabled={loading || aiGenerating || postingToPages}
                    startIcon={<ArrowBack />}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </CardContent>
      </Card>
      
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={() => setAlert(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={() => setAlert(prev => ({ ...prev, open: false }))} 
          severity={alert.severity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default JobPosting;